import * as Sentry from '@sentry/react';
import { selectDeviceType } from 'features/application/applicationSlice';
import { selectIsSystemSession } from 'features/application/selectors';
import { E2EEManager } from 'features/e2ee/E2EEManager';
import { SFrameManager } from 'features/e2ee/SFrameManager';
import { selectMaximizedStream } from 'features/layout/features/content/contentSlice';
import { remoteVideoRatioChanged } from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { selectTileWidth } from 'features/layout/features/dimensions/dimensionsSlice';
import {
  selectHistoricalSpeakers,
  selectOrderedStreams,
  selectScreenshareStreams,
} from 'features/layout/features/order/orderSlice';
import {
  remoteMediaPlaybackBlockedStateChanged,
  remoteFeedDisconnected,
  remoteFeedUpdated,
} from 'features/streaming/streamingSlice';
import {
  selectSelectedScreenshare,
  selectShowAllScreens,
} from 'features/screenshare/screenshareSlice';
import {
  FeedId,
  MediaStreamType,
  PublisherMid,
  RemoteFeedStream,
  RemoteFeedUIState,
  RemoteStreamsByFeedId,
  SubscriberMid,
  SubscribeStreamList,
} from 'features/streaming/types';
import { selectActiveMediaDevice } from 'features/user-media/userMediaSlice';
import { UserId } from 'features/users/types';
import Janus, { JanusJS } from 'lib/janus';
import invert from 'lodash.invert';
import { MutableRefObject } from 'react';
import { store } from 'store/store';
import { formatErrorForLogging, logger } from 'utils/logger';
import { emitTelemetryEvent } from 'utils/sentry/telemetryEvent';
import {
  AudioElement,
  ConfigureSimulcastTemplate,
  E2EEQueueUpdateOptions,
  E2EEReceivingQueue,
  FeedStreamInfo,
  JanusConnection,
  ReceivingFeedStreamSendingState,
  RTCClient,
  StreamSubscriptionState,
} from 'utils/webrtc';
import { AudioPool, UnlockedAudio } from 'utils/audio/AudioPool';
import { AudioElementMonitor } from 'utils/webrtc/diagnostics/AudioElementMonitor';
import { MediaList } from 'utils/webrtc/receiving/MediaList';
import { StreamingReceiver } from 'utils/webrtc/receiving/StreamingReceiver';
import { VideoroomReceiver } from 'utils/webrtc/receiving/VideoroomReceiver';
import { v4 as uuidv4 } from 'uuid';
import throttle from 'lodash.throttle';
import { isErrorShaped } from 'utils/isErrorShaped';
import { logMediaStreamsDiagnostics } from './diagnostics/audioStreamsDiagnostics';
import { extractMediaStreamState } from './diagnostics/streamStateExtractor';

import { ControlledReceivingHandle, VideoroomConnectionState } from './ControlledReceivingHandle';

import { isUserGestureError } from './isUserGestureError';
import { sendMessage, updateSimulcastTemplate } from './messages';
import { StreamKind } from './types';

export type AvailableScreenshares = Record<FeedId, { mid: string; type: MediaStreamType }[]>;

type NavigatorWithConnection = Navigator & {
  connection?: { saveData?: boolean };
};

const PLAY_TIMEOUT_MS = 1000;
const MAX_AUDIO_RECOVERY_ATTEMPTS = 3;

type AudioPlayOutcome = { ok: true } | { ok: false; error: unknown } | { ok: 'timeout' };

// 'aborted' is not a failure: the element changed hands during an await, or a newer load
// interrupted its play().
export type AudioAttachOutcome = 'played' | 'timeout' | 'blocked' | 'failed' | 'aborted';

const isAbortError = (error: unknown) => isErrorShaped(error) && error.name === 'AbortError';

export type AudioReplaceRestartOutcome = AudioAttachOutcome | 'skipped';

type AudioReplaceRestartAttributes = {
  mid: SubscriberMid;
  feedId: FeedId;
  outcome: AudioReplaceRestartOutcome;
  pausedBefore: boolean;
  endedBefore: boolean;
  readyStateBefore: number;
  currentTimeBefore: number;
  newTrackMuted: boolean;
  elementPooled: boolean;
  priorRecoveryAttempts: number;
};

const AUDIO_REPLACE_RESTART_MESSAGE = '[AUDIO-PLAYBACK] restart after track replacement';

const emitAudioReplaceRestart = (attributes: AudioReplaceRestartAttributes) =>
  emitTelemetryEvent({
    event: 'audio.replace.restart',
    message: `${AUDIO_REPLACE_RESTART_MESSAGE} — ${attributes.outcome}`,
    // Level is severity: a timeout is the normal shape of a relay rebuild slower than
    // PLAY_TIMEOUT_MS (the track arrives before DTLS connects) and stays at info.
    level: attributes.outcome === 'blocked' || attributes.outcome === 'failed' ? 'warn' : 'info',
    channel: 'streaming',
    cap: 100,
    attributes: {
      ...attributes,
      receiveMode: RTCClient.receiveMode,
      isSystemSession: selectIsSystemSession(store.getState()),
      msSincePageLoad: Math.round(performance.now()),
    },
  });

type PlayResult<E extends HTMLMediaElement> =
  | { mid: SubscriberMid; element: E; ok: true }
  | { mid: SubscriberMid; element: E; ok: false }
  | { mid: SubscriberMid; element: E; ok: 'timeout' };

export class ReceivingFeed {
  handle: string = uuidv4();

  instanceId: string = uuidv4();

  feedIdByMid: Record<SubscriberMid, FeedId> = {};

  audioElements: Record<SubscriberMid, AudioElement> = {};

  videoElements: Record<SubscriberMid, HTMLVideoElement> = {};

  private audioMonitors: Record<SubscriberMid, AudioElementMonitor> = {};

  private audioRecoveryAttempts: Record<SubscriberMid, number> = {};

  // Keyed by mid:feedId so the healthTimer's +5s duplicate of one wedge is suppressed while
  // a later wedge on the same mid serving a different feed still captures.
  private audioStuckReported: Record<string, true> = {};

  private audioStuckKey = (mid: SubscriberMid, feedId: FeedId) => `${mid}:${feedId}`;

  // These two keys must always clear together — a stranded audioStuckReported entry
  // permanently suppresses the Sentry capture for that mid:feedId.
  private clearAudioRecoveryState = (mid: SubscriberMid, feedId: FeedId) => {
    delete this.audioRecoveryAttempts[mid];
    delete this.audioStuckReported[this.audioStuckKey(mid, feedId)];
  };

  // A cleaned-up instance is reused after a reconnect and non-pooled elements survive cleanup,
  // so a teardown is detected by generation, not identity alone.
  private attachSuperseded = (mid: SubscriberMid, element: AudioElement, generation: number) =>
    this.cleanupGeneration !== generation || this.audioElements[mid] !== element;

  // Reported on the audio-stuck signal to gauge how often the join-burst setSinkId race
  // stranded playback.
  private sinkIdAbortedByMid: Record<SubscriberMid, boolean> = {};

  private videoHealthTimers: Record<SubscriberMid, ReturnType<typeof setTimeout>> = {};

  receiver: VideoroomReceiver | StreamingReceiver | null = null;

  media: MediaList = new MediaList();

  availableScreenshares: AvailableScreenshares = {};

  subscribedScreenshares: Record<FeedId, boolean> = {};

  e2eeQueue: E2EEReceivingQueue = {};

  private blockedAudioElements = new Map<SubscriberMid, HTMLAudioElement>();

  private blockedVideoElements = new Map<SubscriberMid, HTMLVideoElement>();

  private disposed = false;

  private cleanupGeneration = 0;

  constructor() {
    document.addEventListener('touchend', this.retryBlockedMedia, true);
    document.addEventListener('click', this.retryBlockedMedia, true);
  }

  /**
   * Projects the blocked-element Maps (the source of truth) into the store as a full
   * snapshot. Called after every Map mutation; the reducer no-ops on identical
   * snapshots, so redundant calls are safe. Video is split by owner document because
   * recovery differs: elements in the Document PiP window need a PiP-realm gesture,
   * main-document elements need a main-tab gesture.
   */
  private syncBlockedMediaStateToStore = () => {
    let mainDocumentVideo = false;
    let pipDocumentVideo = false;

    this.blockedVideoElements.forEach((element) => {
      if (element.ownerDocument === document) {
        mainDocumentVideo = true;
      } else {
        pipDocumentVideo = true;
      }
    });

    store.dispatch(
      remoteMediaPlaybackBlockedStateChanged({
        audio: this.blockedAudioElements.size > 0,
        mainDocumentVideo,
        pipDocumentVideo,
      })
    );
  };

  retryBlockedAudio = () => {
    if (this.blockedAudioElements.size === 0) {
      return;
    }

    // Snapshot entries but do not clear the Map — it remains the source of truth
    // for which elements are still blocked. Entries are deleted only on successful play().
    const pending = [...this.blockedAudioElements.entries()];

    // CRITICAL: kick off every play() in the same tick — splitting with await would
    // lose the user-gesture activation token for elements past the first.
    const playPromises = pending.map(([mid, element]): Promise<PlayResult<HTMLAudioElement>> => {
      const timeout = new Promise<PlayResult<HTMLAudioElement>>((resolve) => {
        setTimeout(() => resolve({ mid, element, ok: 'timeout' }), PLAY_TIMEOUT_MS);
      });

      const play = element
        .play()
        .then(() => ({ mid, element, ok: true as const }))
        .catch(() => ({ mid, element, ok: false as const }));

      return Promise.race([play, timeout]);
    });

    // Each promise has its own .catch — Promise.all never rejects.
    // eslint-disable-next-line no-void
    void Promise.all(playPromises).then(async (results) => {
      if (this.disposed) {
        return;
      }

      const sinkId = selectActiveMediaDevice(store.getState(), 'audiooutput');
      let recovered = 0;
      let timedOut = 0;

      for (const result of results) {
        // rejection: leave entry for next gesture
        if (result.ok !== false) {
          // Stale-element guard (applies to both success and timeout paths):
          // unloadAudio may have removed or replaced this entry while play() was in
          // flight. Only proceed if the Map still has the element we tracked.
          if (this.blockedAudioElements.get(result.mid) === result.element) {
            this.blockedAudioElements.delete(result.mid);

            if (result.ok === true) {
              recovered += 1;
              // eslint-disable-next-line no-await-in-loop
              await this.setAudioElementOutput(result.element, sinkId).catch(() => {
                // Error already logged in setAudioElementOutput.
              });
            } else {
              // result.ok === 'timeout': the user gave consent; we honor it by clearing
              // the Map slot even though play() did not observably settle. The element
              // will continue trying on its own; if it never recovers, that's a separate
              // audio-pipeline bug, not a modal bug — the UI is no longer held hostage.
              timedOut += 1;
            }
          }
        }
      }

      logger
        .remote({ system: true, capture: 'streaming' })
        .info('[AUDIO-PLAYBACK] recovery attempt completed', {
          attempted: pending.length,
          recovered,
          timedOut,
          stillBlocked: this.blockedAudioElements.size,
        });

      this.syncBlockedMediaStateToStore();
    });
  };

  retryBlockedVideo = () => {
    if (this.blockedVideoElements.size === 0) {
      return;
    }

    // Snapshot entries but do not clear the Map — it remains the source of truth
    // for which elements are still blocked. Entries are deleted only on successful play().
    const pending = [...this.blockedVideoElements.entries()];

    // CRITICAL: kick off every play() in the same tick — splitting with await would
    // lose the user-gesture activation token for elements past the first.
    const playPromises = pending.map(([mid, element]): Promise<PlayResult<HTMLVideoElement>> => {
      const timeout = new Promise<PlayResult<HTMLVideoElement>>((resolve) => {
        setTimeout(() => resolve({ mid, element, ok: 'timeout' }), PLAY_TIMEOUT_MS);
      });

      const play = element
        .play()
        .then(() => ({ mid, element, ok: true as const }))
        .catch(() => ({ mid, element, ok: false as const }));

      return Promise.race([play, timeout]);
    });

    // eslint-disable-next-line no-void
    void Promise.all(playPromises).then((results) => {
      if (this.disposed) {
        return;
      }

      let recovered = 0;
      let timedOut = 0;

      for (const result of results) {
        // rejection: leave entry for next gesture
        if (result.ok !== false) {
          // Stale-element guard: detachFromElement may have removed or replaced this
          // entry while play() was in flight. Applies to both success and timeout.
          if (this.blockedVideoElements.get(result.mid) === result.element) {
            this.blockedVideoElements.delete(result.mid);
            if (result.ok === true) {
              recovered += 1;
            } else {
              timedOut += 1;
            }
          }
        }
      }

      logger
        .remote({ system: true, capture: 'streaming' })
        .info('[VIDEO-PLAYBACK] recovery attempt completed', {
          attempted: pending.length,
          recovered,
          timedOut,
          stillBlocked: this.blockedVideoElements.size,
        });

      this.syncBlockedMediaStateToStore();
    });
  };

  /**
   * Document-level gesture handler (touchend + click capture-phase). Drains both
   * blocked-media Maps in the same synchronous tick to preserve the user-activation
   * token across every play() call.
   */
  retryBlockedMedia = () => {
    this.retryBlockedAudio();
    this.retryBlockedVideo();
  };

  private recordBlockedAudio = (mid: SubscriberMid, element: HTMLAudioElement, feedId: FeedId) => {
    this.blockedAudioElements.set(mid, element);

    logger
      .remote({ system: true, capture: 'streaming' })
      .warn('[AUDIO-PLAYBACK] play() blocked, awaiting user gesture', {
        mid,
        feedId,
        elementId: element.id,
        blockedCount: this.blockedAudioElements.size,
      });

    this.syncBlockedMediaStateToStore();
  };

  private recordBlockedVideo = (mid: SubscriberMid, element: HTMLVideoElement, feedId: FeedId) => {
    this.blockedVideoElements.set(mid, element);

    logger
      .remote({ system: true, capture: 'streaming' })
      .warn('[VIDEO-PLAYBACK] play() blocked, awaiting user gesture', {
        mid,
        feedId,
        elementId: element.id,
        pipDocumentOrigin: element.ownerDocument !== document,
        blockedCount: this.blockedVideoElements.size,
      });

    this.syncBlockedMediaStateToStore();
  };

  /**
   * Wraps every video `play()` call to surface its outcome. The shipped
   * `attachVideo` has three play() sites (FF onunmute callback, onloadeddata
   * callback, Save-Data synchronous path); two are fire-and-forget callbacks
   * whose rejections previously escaped to the global unhandled-rejection
   * handler. Routing all three through this adapter is the only way every
   * outcome lands somewhere we can observe.
   */
  private handleVideoPlayPromise = (
    mid: SubscriberMid,
    feedId: FeedId,
    element: HTMLVideoElement,
    playPromise: Promise<void>
  ) => {
    playPromise
      .then(() => {
        logger
          .remote({ system: true, capture: 'streaming' })
          .info('[VIDEO-PLAYBACK] play() succeeded', {
            mid,
            feedId,
            elementId: element.id,
          });
      })
      .catch((error) => {
        if (isUserGestureError(error)) {
          this.recordBlockedVideo(mid, element, feedId);
          return;
        }

        if (isErrorShaped(error) && error.message.includes('was interrupted')) {
          // pause-during-play race; benign — element will be re-played on next attach.
          return;
        }

        logger
          .remote({ system: true, capture: 'streaming' })
          .error('[VIDEO-PLAYBACK] play() failed', {
            mid,
            feedId,
            elementId: element.id,
            ...formatErrorForLogging(error),
          });
        Sentry.captureException(error, {
          tags: { component: 'ReceivingFeed', operation: 'video.play' },
          extra: { mid, feedId, elementId: element.id },
        });
      });
  };

  // redux layer
  private get uiState(): RemoteFeedUIState {
    const streamsByFeedId: RemoteStreamsByFeedId = {};

    this.media.forEachSubscribedFeed((list, feedId) => {
      streamsByFeedId[feedId] = [];

      // What's a memory leak anyway?
      const dedupedStreams: Record<string, RemoteFeedStream> = {};

      list.forEachStream((stream) => {
        if (dedupedStreams[stream.kind]) {
          logger
            .remote({ system: true, capture: 'streaming' })
            .error(
              `Caught duplicate ${stream.kind} stream in ${feedId}, dupe mid ${
                dedupedStreams[stream.kind].mid
              }`
            );
        }

        if (stream.subscriberMid) {
          const simulcast = this.media.subscriberStream(feedId, stream.subscriberMid)?.simulcast;

          dedupedStreams[stream.kind] = {
            kind: stream.kind,
            mid: stream.subscriberMid,
            substream: simulcast?.substream,
            temporal: simulcast?.temporal,
          };
        }
      });

      streamsByFeedId[feedId] = Object.values(dedupedStreams);
    });

    return {
      feedIdByMid: { ...this.feedIdByMid },
      streamsByFeedId,
    };
  }

  setAvailableScreenshare = (feedId: FeedId, streams: { mid: string; type: MediaStreamType }[]) => {
    this.availableScreenshares[feedId] = streams;
  };

  updateAvailableScreenshares = (availableScreenshares: AvailableScreenshares = {}) => {
    this.availableScreenshares = {
      ...this.availableScreenshares,
      ...availableScreenshares,
    };
  };

  getSubscribedScreenshareFeedIds = (): FeedId[] =>
    Object.entries(this.subscribedScreenshares)
      .filter(([, subscribed]) => subscribed)
      .map(([feedId]) => feedId);

  initReceiver = (mode: 'streaming' | 'videoroom') => {
    this.receiver =
      mode === 'streaming' ? new StreamingReceiver(this) : new VideoroomReceiver(this);
  };

  getPeerConnection = (): RTCPeerConnection | undefined =>
    this.receiver?.plugin?.janusPlugin?.webrtcStuff?.pc;

  attachAudio = async (mid: SubscriberMid): Promise<AudioAttachOutcome | null> => {
    const generation = this.cleanupGeneration;
    const feedId = this.feedIdByMid[mid];
    const stream = this.media.subscriberStream(feedId, mid);

    logger
      .remote({ system: true, capture: 'streaming' })
      .debug(`[AUDIO-DEBUG] Started attaching audio to feed`, {
        mid,
        feedId,
        hasStream: !!stream,
        streamHasTrack: stream?.hasTrack,
        streamTrackId: stream?.getTrack()?.id,
        audioElementExists: !!this.audioElements[mid],
      });

    if (!(feedId && stream)) {
      const errorMessage = 'ReceivingFeed: attachAudio failed - missing feed or stream';
      logger.remote({ system: true, capture: 'streaming' }).error(errorMessage, {
        mid,
        feedId,
        hasStream: !!stream,
        streamId: stream?.mediaStream?.id,
        availableFeeds: Object.keys(this.media.streams),
      });

      Sentry.captureMessage(errorMessage, {
        tags: { component: 'ReceivingFeed', operation: 'attachAudio' },
        extra: { mid, feedId, hasStream: !!stream },
      });

      return null;
    }

    // Validate feed is still active - safeguard against stale feed mappings
    if (!this.media.streams[feedId]) {
      const errorMessage = 'ReceivingFeed: attachAudio failed - stale feed mapping detected';
      logger.remote({ system: true, capture: 'streaming' }).error(errorMessage, {
        mid,
        staleFeedId: feedId,
        availableFeeds: Object.keys(this.media.streams),
      });

      Sentry.captureMessage(errorMessage, {
        tags: { component: 'ReceivingFeed', operation: 'attachAudio' },
        extra: { mid, staleFeedId: feedId },
      });

      delete this.feedIdByMid[mid];
      return null;
    }

    let element = this.audioElements[mid];

    // handle unlocked audio only once
    if (!element) {
      const obtained = await AudioPool.obtainElement();
      if (!obtained) {
        const errorMessage = 'ReceivingFeed: failed to obtain audio element from pool';
        logger.remote({ system: true, capture: 'streaming' }).error(errorMessage, {
          mid,
          feedId,
        });

        Sentry.captureMessage(errorMessage, {
          tags: { component: 'ReceivingFeed', operation: 'attachAudio' },
          extra: { mid, feedId },
        });

        return null;
      }

      // obtainElement can yield (ctx.resume()), so a concurrent attach or a cleanup may have
      // landed; release the fresh element rather than leak it from the pool.
      if (this.cleanupGeneration !== generation || this.audioElements[mid]) {
        if (obtained instanceof UnlockedAudio) {
          AudioPool.unloadElement(obtained);
        }
        return null;
      }

      element = obtained;
      this.audioElements[mid] = element;
    }

    try {
      element.srcObject = stream.mediaStream;
    } catch (error) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .error('[AUDIO-PLAYBACK] failed to set srcObject', {
          mid,
          feedId,
          elementId: element.id,
          ...formatErrorForLogging(error),
        });

      return null;
    }

    // Late joiners, reconnects, and pool-recycled elements all pass through here,
    // so the owner tag (and therefore local/global mute policy) is enforced at every attach.
    AudioPool.assignOwner(element, RTCClient.userIdByFeedId[feedId]);

    const audioOutputId = selectActiveMediaDevice(store.getState(), 'audiooutput');

    this.sinkIdAbortedByMid[mid] = false;
    try {
      await this.setAudioElementOutput(element, audioOutputId);

      logger
        .remote({ system: true, capture: 'streaming' })
        .debug('[AUDIO-PLAYBACK] audio output device set during initial attachment', {
          mid,
          feedId,
          elementId: element.id,
          audioOutputId: audioOutputId || 'default',
          sinkId: element.sinkId || 'default',
        });
    } catch (error) {
      if (!isUserGestureError(error)) {
        this.sinkIdAbortedByMid[mid] = true;
        logger
          .remote({ system: true, capture: 'streaming' })
          .warn('[AUDIO-PLAYBACK] failed to set audio output device during initial attachment', {
            mid,
            feedId,
            elementId: element.id,
            requestedDevice: audioOutputId || 'default',
            currentSinkId: element.sinkId || 'default',
            ...formatErrorForLogging(error),
          });
      }
    }

    if (this.attachSuperseded(mid, element, generation)) {
      return 'aborted';
    }

    // Time-box play(): it may never settle, so arm the monitor regardless of the outcome —
    // the monitor is what recovers a stuck element.
    let playTimer: ReturnType<typeof setTimeout> | undefined;
    const playPromise: Promise<AudioPlayOutcome> = element
      .play()
      .then(() => ({ ok: true as const }))
      .catch((error: unknown) => ({ ok: false as const, error }));
    const playOutcome = await Promise.race<AudioPlayOutcome>([
      playPromise,
      new Promise<AudioPlayOutcome>((resolve) => {
        playTimer = setTimeout(() => resolve({ ok: 'timeout' }), PLAY_TIMEOUT_MS);
      }),
    ]);
    if (playTimer) {
      clearTimeout(playTimer);
    }

    // A teardown, or a re-attach holding a different element, owns it now; arming a monitor
    // here would pin one to a pooled element.
    if (this.attachSuperseded(mid, element, generation)) {
      return 'aborted';
    }

    let outcome: AudioAttachOutcome;
    if (playOutcome.ok === true) {
      // Read before the reset: a non-zero count means a prior stuck-audio recovery just succeeded.
      const recoveredAfterReattach = (this.audioRecoveryAttempts[mid] ?? 0) > 0;
      this.clearAudioRecoveryState(mid, feedId);
      logger
        .remote({ system: true, capture: 'streaming' })
        .info('[AUDIO-PLAYBACK] play() succeeded', {
          mid,
          feedId,
          elementId: element.id,
          paused: element.paused,
          volume: element.volume,
          muted: element.muted,
        });
      if (recoveredAfterReattach) {
        logger
          .remote({ system: true, capture: 'streaming' })
          .info('[AUDIO-PLAYBACK] audio stuck — recovered after re-attach', {
            mid,
            feedId,
            elementId: element.id,
          });
      }
      outcome = 'played';
    } else if (playOutcome.ok === 'timeout') {
      logger
        .remote({ system: true, capture: 'streaming' })
        .warn(
          '[AUDIO-PLAYBACK] play() did not settle within timeout — monitor will track recovery',
          {
            mid,
            feedId,
            elementId: element.id,
            timeoutMs: PLAY_TIMEOUT_MS,
          }
        );
      // Timeout discards the raced promise; observe it anyway so a late settle still logs.
      // eslint-disable-next-line no-void
      void playPromise.then((settled) => {
        const priorRecoveryAttempts = this.audioRecoveryAttempts[mid] ?? 0;
        if (settled.ok === true) {
          // Late success (e.g. WebKit releasing its background media-load gate) must reset
          // the budget, or the next wedge on this mid exhausts with zero real attempts.
          this.clearAudioRecoveryState(mid, feedId);
        }
        let settledOutcome: 'played' | 'aborted' | 'failed';
        if (settled.ok === true) {
          settledOutcome = 'played';
        } else if (settled.ok === false && isAbortError(settled.error)) {
          settledOutcome = 'aborted';
        } else {
          settledOutcome = 'failed';
        }
        logger
          .remote({ system: true, capture: 'streaming' })
          .info('[AUDIO-PLAYBACK] play() settled after timeout', {
            mid,
            feedId,
            elementId: element.id,
            outcome: settledOutcome,
            priorRecoveryAttempts,
          });
      });
      outcome = 'timeout';
    } else if (isUserGestureError(playOutcome.error)) {
      this.recordBlockedAudio(mid, element, feedId);
      outcome = 'blocked';
    } else if (isAbortError(playOutcome.error)) {
      // pause() or a newer load interrupted play(); the interrupting attach plays the element.
      logger
        .remote({ system: true, capture: 'streaming' })
        .info('[AUDIO-PLAYBACK] play() aborted', {
          mid,
          feedId,
          elementId: element.id,
        });
      outcome = 'aborted';
    } else {
      logger
        .remote({ system: true, capture: 'streaming' })
        .error('[AUDIO-PLAYBACK] play() failed', {
          mid,
          feedId,
          elementId: element.id,
          ...formatErrorForLogging(playOutcome.error),
        });
      Sentry.captureException(playOutcome.error, {
        tags: { component: 'ReceivingFeed', operation: 'play' },
        extra: { mid, feedId, elementId: element.id },
      });
      outcome = 'failed';
    }

    const monitor = this.audioMonitors[mid] ?? new AudioElementMonitor();
    this.audioMonitors[mid] = monitor;
    monitor.attach(
      mid,
      element,
      () => {
        const currentFeedId = this.feedIdByMid[mid];
        if (!currentFeedId) {
          return null;
        }
        const currentStream = this.media.peekSubscriberStream(currentFeedId, mid);
        const currentElement = this.audioElements[mid];
        if (!currentStream || !currentElement) {
          return null;
        }
        return { feedId: currentFeedId, stream: currentStream, element: currentElement };
      },
      this.recoverStuckAudio
    );

    // play() timed out with RTP already flowing → the element is stuck now; run the health
    // check immediately instead of waiting the full monitor timer.
    if (playOutcome.ok === 'timeout' && stream.getTrack()?.muted === false) {
      monitor.checkNow();
    }

    return outcome;
  };

  private recoverStuckAudio = (mid: SubscriberMid) => {
    // Gesture-blocked elements recover via retryBlockedAudio; re-attaching would fight it.
    if (this.blockedAudioElements.has(mid)) {
      return;
    }

    const feedId = this.feedIdByMid[mid];
    if (!feedId) {
      return;
    }

    const sinkIdAborted = this.sinkIdAbortedByMid[mid] ?? false;
    const concurrentAudioElements = Object.keys(this.audioElements).length;
    const element = this.audioElements[mid];
    const elementEverLoaded = element instanceof UnlockedAudio ? element.hasEverLoaded : null;
    const { hidden } = document;

    // Population signal: every element found stuck-but-live at the 5s health check. High-volume,
    // so log only — the exhausted branch below is what raises the Sentry alert.
    logger
      .remote({ system: true, capture: 'streaming' })
      .info('[AUDIO-PLAYBACK] audio stuck — detected', {
        mid,
        feedId,
        priorRecoveryAttempts: this.audioRecoveryAttempts[mid] ?? 0,
        sinkIdAborted,
        concurrentAudioElements,
        hidden,
        elementEverLoaded,
      });

    const attempt = (this.audioRecoveryAttempts[mid] ?? 0) + 1;
    if (attempt > MAX_AUDIO_RECOVERY_ATTEMPTS) {
      const message = '[AUDIO-PLAYBACK] audio stuck — recovery exhausted';
      logger.remote({ system: true, capture: 'streaming' }).error(message, {
        mid,
        feedId,
        attempts: MAX_AUDIO_RECOVERY_ATTEMPTS,
        sinkIdAborted,
        concurrentAudioElements,
        hidden,
        elementEverLoaded,
      });
      const stuckKey = this.audioStuckKey(mid, feedId);
      if (!this.audioStuckReported[stuckKey]) {
        this.audioStuckReported[stuckKey] = true;
        Sentry.captureMessage(message, {
          level: 'error',
          tags: {
            component: 'ReceivingFeed',
            operation: 'audio-stuck',
            page_hidden: String(hidden),
            element_ever_loaded: String(elementEverLoaded),
          },
          extra: {
            mid,
            feedId,
            attempts: MAX_AUDIO_RECOVERY_ATTEMPTS,
            sinkIdAborted,
            concurrentAudioElements,
            hidden,
            elementEverLoaded,
          },
        });
      }
      return;
    }
    this.audioRecoveryAttempts[mid] = attempt;

    logger
      .remote({ system: true, capture: 'streaming' })
      .warn('[AUDIO-PLAYBACK] audio attached but not playing — resetting and re-attaching', {
        mid,
        feedId,
        attempt,
      });

    // eslint-disable-next-line no-void
    void this.reattachAudio(mid);
  };

  // unloadAudio resets the element via pause()+load(). The map is cleared unconditionally —
  // a non-pooled element survives unloadAudio — and both run before any await (one-restart invariant).
  private reattachAudio = (mid: SubscriberMid) => {
    this.unloadAudio(mid);
    delete this.audioElements[mid];
    return this.attachAudio(mid);
  };

  private restartAudioAfterReplace = async (
    mid: SubscriberMid,
    feedId: FeedId,
    newTrack: MediaStreamTrack
  ) => {
    const element = this.audioElements[mid];
    // No element yet: the mount effect's own attachAudio is still to come.
    if (!element) {
      return;
    }
    // The gesture retry owns a blocked element and plays whatever the stream holds by then.
    if (this.blockedAudioElements.has(mid)) {
      return;
    }

    const snapshot = {
      pausedBefore: element.paused,
      endedBefore: element.ended,
      readyStateBefore: element.readyState,
      currentTimeBefore: element.currentTime,
      newTrackMuted: newTrack.muted,
      elementPooled: element instanceof UnlockedAudio,
    };
    const priorRecoveryAttempts = this.audioRecoveryAttempts[mid] ?? 0;

    // Not `paused` alone: a remount's play() in flight on the ended clone has paused=false
    // with readyState < 2.
    const rendering = !element.paused && element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
    if (rendering) {
      emitAudioReplaceRestart({
        mid,
        feedId,
        outcome: 'skipped',
        ...snapshot,
        priorRecoveryAttempts,
      });
      return;
    }

    const outcome = await this.reattachAudio(mid);
    // null: the attach either reported its own failure or was overtaken by a concurrent
    // attach or a cleanup — no outcome to report.
    if (outcome === null) {
      return;
    }

    emitAudioReplaceRestart({ mid, feedId, outcome, ...snapshot, priorRecoveryAttempts });
  };

  // update audio output on state change in a listener
  updateAudioOutput = (id: string) => {
    logger
      .remote({ system: true, capture: 'streaming' })
      .debug('[AUDIO-OUTPUT] Updating audio output devices', {
        targetDevice: id || 'default',
        affectedElements: Object.keys(this.audioElements).length,
      });

    for (const [mid, element] of Object.entries(this.audioElements)) {
      if (!element) {
        const errorMessage = 'ReceivingFeed: null audio element found during output update';
        logger.remote({ system: true, capture: 'streaming' }).warn(errorMessage, {
          mid,
          audioOutputId: id,
        });

        Sentry.captureMessage(errorMessage, {
          level: 'warning',
          tags: { component: 'ReceivingFeed', operation: 'updateAudioOutput' },
          extra: { mid, audioOutputId: id },
        });

        return;
      }

      const feedId = this.feedIdByMid[mid];

      this.setAudioElementOutput(element, id)
        .then(() => {
          logger
            .remote({ system: true, capture: 'streaming' })
            .debug('[AUDIO-OUTPUT] Audio output device updated successfully', {
              mid,
              feedId,
              elementId: element.id,
              audioOutputId: id || 'default',
              sinkId: element.sinkId || 'default',
            });
        })
        .catch(() => {
          // Error already logged in setAudioElementOutput
        });
    }
  };

  attachVideo = (mid: SubscriberMid, ref: MutableRefObject<HTMLVideoElement | null>) => {
    const feedId = this.feedIdByMid[mid];
    const stream = this.media.subscriberStream(feedId, mid);

    if (!(feedId && stream)) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .warn('[VIDEO-PLAYBACK] attachVideo skipped — no feed or active stream', { mid });
      return;
    }

    if (!ref.current) {
      return;
    }

    const el = ref.current;

    logger
      .remote({ system: true, capture: 'streaming' })
      .debug('[VIDEO-PLAYBACK] attaching video', { mid, feedId, elementId: el.id });

    const isFF = Janus.webRTCAdapter.browserDetails.browser === 'firefox';
    const needFFHack = isFF && this.receiver instanceof StreamingReceiver;
    const track = stream.mediaStream.getVideoTracks()?.[0];

    // Narrow try around the only synchronous-throw surface: srcObject assignment.
    try {
      if (needFFHack && track?.muted) {
        track.onunmute = () => {
          el.onloadedmetadata = null;
          el.srcObject = null;
          el.srcObject = stream.mediaStream;
          this.handleVideoPlayPromise(mid, feedId, el, el.play());
        };
      } else {
        el.onloadedmetadata = null;
        el.srcObject = null;
        el.srcObject = stream.mediaStream;
      }
    } catch (error) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .error('[VIDEO-PLAYBACK] failed to set srcObject', {
          mid,
          feedId,
          elementId: el.id,
          ...formatErrorForLogging(error),
        });
      Sentry.captureException(error, {
        tags: { component: 'ReceivingFeed', operation: 'attachVideo.srcObject' },
        extra: { mid, feedId, elementId: el.id },
      });
      return;
    }

    el.onloadeddata = () => {
      if (ref.current) {
        this.handleVideoPlayPromise(mid, feedId, ref.current, ref.current.play());
      }
    };

    if (isFF) {
      el.addEventListener(
        'timeupdate',
        throttle(
          () => {
            if (ref.current) {
              store.dispatch(
                remoteVideoRatioChanged({
                  feedId,
                  ratio: ref.current.videoHeight / ref.current.videoWidth,
                })
              );
            }
          },
          1000,
          { leading: true }
        )
      );
    } else {
      el.onloadedmetadata = () => {
        if (ref.current) {
          store.dispatch(
            remoteVideoRatioChanged({
              feedId,
              ratio: ref.current.videoHeight / ref.current.videoWidth,
            })
          );
        }
      };
    }

    // Bypass Save-Data setting since "loadeddata" doesn't fire on mobile with this setting.
    const { connection } = navigator as NavigatorWithConnection;
    if (connection?.saveData === true) {
      this.handleVideoPlayPromise(mid, feedId, el, el.play());
    }

    this.videoElements[mid] = el;

    // Debounced 5 s [VIDEO-HEALTH] check — body unchanged from the shipped version.
    clearTimeout(this.videoHealthTimers[mid]);
    this.videoHealthTimers[mid] = setTimeout(() => {
      delete this.videoHealthTimers[mid];

      const videoElement = this.videoElements[mid];
      const currentFeedId = this.feedIdByMid[mid];
      if (!videoElement || !currentFeedId) {
        return;
      }

      const subscriberStream = this.media.subscriberStream(currentFeedId, mid);
      if (!subscriberStream) {
        return;
      }

      const diagnostics = extractMediaStreamState(
        currentFeedId,
        mid,
        subscriberStream,
        videoElement
      );
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug('[VIDEO-HEALTH] 5s health check after attach', diagnostics);
    }, 5000);
  };

  detachFromElement = (mid: SubscriberMid, ref?: MutableRefObject<HTMLMediaElement | null>) => {
    const feedId = this.feedIdByMid[mid];
    if (!feedId) {
      return;
    }

    const element = (ref?.current as HTMLVideoElement | null) ?? this.videoElements[mid];

    const isMediaBroadcasting = RTCClient.broadcastingByUserId[RTCClient.userIdByFeedId[feedId]];

    if (isMediaBroadcasting) {
      Sentry.addBreadcrumb({
        type: 'streaming',
        message: 'Media removed from UI is considered to be enabled',
        data: { feedId, mid },
      });
    }

    if (ref?.current) {
      ref.current.pause();
      ref.current.onloadeddata = null;
    }

    clearTimeout(this.videoHealthTimers[mid]);
    delete this.videoHealthTimers[mid];
    delete this.videoElements[mid];

    const wasBlocked = this.blockedVideoElements.delete(mid);
    if (wasBlocked) {
      this.syncBlockedMediaStateToStore();
    }

    logger
      .remote({ system: true, capture: 'streaming' })
      .debug('[VIDEO-PLAYBACK] Video element detached', {
        mid,
        feedId,
        elementId: element?.id,
        paused: element?.paused,
        currentTime: element?.currentTime,
        wasBlocked,
      });
  };

  unloadFeedAudio = (userId: UserId) => {
    const feedIdByUserId = invert(RTCClient.userIdByFeedId);
    const feedId = feedIdByUserId[userId];

    if (feedId) {
      this.media.getFeedStreams(feedId).forEachStream((stream) => {
        if (stream.kind === 'audio' && stream.subscriberMid) {
          this.unloadAudio(stream.subscriberMid);
          this.clearAudioRecoveryState(stream.subscriberMid, feedId);
          delete this.sinkIdAbortedByMid[stream.subscriberMid];
        }
      });
    }
  };

  handleSubscriptionsUpdate = (
    feed: ControlledReceivingHandle,
    streams: JanusJS.StreamUpdate[]
  ) => {
    const feedIdByMid: Record<SubscriberMid, FeedId> = {};
    const moves: Array<[SubscriberMid, SubscriberMid, FeedId]> = [];

    streams.forEach((stream) => {
      feedIdByMid[stream.mid] = stream.feed_id;

      if (feedIdByMid[stream.mid] === undefined && this.feedIdByMid[stream.mid]) {
        feedIdByMid[stream.mid] = this.feedIdByMid[stream.mid];
      }

      if (stream.active) {
        const previousMid = this.media.existingStream(
          stream.feed_id,
          stream.feed_mid
        )?.subscriberMid;
        if (previousMid !== undefined && previousMid !== false && previousMid !== stream.mid) {
          moves.push([previousMid, stream.mid, stream.feed_id]);
        }

        // update pub<->sub mid map in list of what's available;
        this.media.setSubscriberMid(stream.feed_id, stream.feed_mid, stream.mid);
        // setSubscriberMid may auto-create the record (revived after stale-media cleanup);
        // stamp the reported kind or the StreamMedia default 'audio' mislabels video m-lines.
        this.media.setKind(stream.feed_id, stream.feed_mid, stream.type);
      }
    });

    this.rekeySubscriberMids(moves);

    // See if it slows down UI too much or is acceptable;
    this.feedIdByMid = feedIdByMid;
    this.updateRedux();
  };

  /** `{ feed, mid: publisherMid }` for every stream that held a subscriber mid on the old PC, with
   *  each stream's subscriptionState reset to `created` so `subscribe` accepts it again. */
  streamsForRejoin = (): SubscribeStreamList => {
    const streams: SubscribeStreamList = [];

    this.media.forEachSubscribedFeed((list, feedId) => {
      list.forEachStream((stream, publisherMid) => {
        if (stream.subscriberMid === false) {
          return;
        }

        stream.setSubscriptionState(StreamSubscriptionState.created);
        streams.push({ feed: feedId, mid: publisherMid });
      });
    });

    return streams;
  };

  // A fresh subscriber handle gets new mids from Janus; `useRemoteAudio`'s cleanup keys off
  // `feedIdByMid[old]`, which is now gone, so the stale element would leak unless moved.
  private rekeySubscriberMids = (moves: Array<[SubscriberMid, SubscriberMid, FeedId]>) => {
    if (!moves.length) {
      return;
    }

    const rekey = <T>(record: Record<SubscriberMid, T>) => {
      const snapshot = { ...record };
      moves.forEach(([from]) => {
        delete record[from];
      });
      moves.forEach(([from, to]) => {
        if (from in snapshot) {
          record[to] = snapshot[from];
        }
      });
    };

    rekey(this.audioElements);
    rekey(this.audioMonitors);
    rekey(this.audioRecoveryAttempts);
    rekey(this.sinkIdAbortedByMid);
    rekey(this.videoElements);

    // The health-check callback closes over the mid it was scheduled for; moving its id to the
    // new key would strand it there, since the callback would still act on the old mid.
    moves.forEach(([from]) => {
      clearTimeout(this.videoHealthTimers[from]);
      delete this.videoHealthTimers[from];
    });

    // A rebuilt PC is a genuinely new wedge candidate; carrying the suppression flag forward
    // would silence the Sentry capture this feature exists to collect for it.
    moves.forEach(([from, , feedId]) => {
      delete this.audioStuckReported[this.audioStuckKey(from, feedId)];
    });

    logger
      .remote({ system: true, capture: 'streaming' })
      .info('Subscriber mids re-keyed after re-join', { moves });
  };

  addAvailableStreams = (feedId: FeedId, streams: FeedStreamInfo[]) => {
    const localMedia = feedId === RTCClient.publishingFeed.handle?.feedId;
    const localScreenshare = feedId === RTCClient.screensharingFeed.handle?.feedId;

    if ((localMedia || localScreenshare) && this.receiver instanceof StreamingReceiver) {
      this.receiver.stopLocalStreams(streams.map((stream) => ({ mid: stream.mid, send: false })));
    }

    streams?.forEach((stream) => {
      const streamMedia = this.media.stream(feedId, stream.mid);
      streamMedia.setKind(stream.type);
    });
  };

  attachToConnection = (connection: JanusConnection) => {
    const videoroomHandle = uuidv4();

    this.receiver?.attachPlugin(videoroomHandle, connection);
  };

  // internals

  prepareSubscription = (connectionHandle: string, streams: SubscribeStreamList) => {
    Sentry.addBreadcrumb({
      category: 'streaming',
      message: 'prepareSubscription call',
      data: { streams },
    });

    if (E2EEManager.e2eeEnabled) {
      streams = this.prepareE2EESubscription(connectionHandle, streams);
    }

    const requestedStreams: SubscribeStreamList = [];
    const validStreams: { feed: string; mid?: string }[] = [];
    const invalidStreams: { feed: string; mid?: string; state: StreamSubscriptionState }[] = [];

    streams?.forEach((stream) => {
      // Streams without `mid` are always valid
      if (!stream.mid) {
        validStreams.push(stream);
        requestedStreams.push(stream);
        return;
      }

      const streamInfo = this.media.stream(stream.feed, stream.mid);

      // Don't include audio streams that are already subscribed
      const isAudioAlreadySubscribed = streamInfo.kind === 'audio' && !streamInfo.canSubscribe;
      if (!isAudioAlreadySubscribed) {
        requestedStreams.push(stream);
      }

      if (streamInfo.canSubscribe) {
        validStreams.push(stream);
        return;
      }

      if (streamInfo.kind === 'audio') {
        logger
          .remote({ system: true, capture: 'streaming' })
          .debug(
            `[AUDIO-DEBUG] Requested audio stream filtered out during subscription preparation: (existing stream is already subscribed)`,
            {
              feedId: stream.feed,
              mid: stream.mid,
              subscriptionState: streamInfo.subscriptionState,
              hasTrack: streamInfo.hasTrack,
              trackId: streamInfo.getTrack()?.id,
              trackReadyState: streamInfo.getTrack()?.readyState,
            }
          );
      }

      if (streamInfo.kind === 'video') {
        invalidStreams.push({
          ...stream,
          state: streamInfo.subscriptionState,
        });
      }
    });

    if (requestedStreams.length) {
      logger
        .remote({ tier: 1 })
        .warn(
          'Requested streams:',
          requestedStreams,
          'Valid streams:',
          validStreams,
          'Invalid streams:',
          invalidStreams
        );
    }

    return validStreams;
  };

  requestSubscription = async (connectionHandle: string, requestedStreams: SubscribeStreamList) => {
    const streams = this.prepareSubscription(connectionHandle, requestedStreams);

    if (streams.length) {
      logger
        .remote({ tier: 1 })
        .info(
          `Subscribing to ${streams.length} streams: `,
          streams,
          'requested streams:',
          requestedStreams
        );

      await this.receiver?.subscribe(connectionHandle, streams);
    }
  };

  unsubscribe = (feedId: FeedId) => {
    if (!this.receiver?.plugin) {
      return;
    }

    const cleanedUpMids: SubscriberMid[] = [];

    for (const [mid, mappedFeedId] of Object.entries(this.feedIdByMid)) {
      if (mappedFeedId === feedId) {
        const hasAudioElement = !!this.audioElements[mid];

        delete this.feedIdByMid[mid];
        // Mid is gone; drop its per-mid recovery/sink state so a later reuse starts clean
        // (not in unloadAudio — recoverStuckAudio calls that and must keep the attempt count).
        this.clearAudioRecoveryState(mid, feedId);
        delete this.sinkIdAbortedByMid[mid];
        cleanedUpMids.push(mid);

        if (hasAudioElement) {
          this.unloadAudio(mid);
        }

        if (this.videoElements[mid]) {
          clearTimeout(this.videoHealthTimers[mid]);
          delete this.videoHealthTimers[mid];
          delete this.videoElements[mid];
        }
      }
    }

    this.media.deleteFeed(feedId);

    RTCClient.disconnectFeed(feedId);

    this.receiver.unsubscribe(feedId);

    logger.remote().info(`Unsubscribed from remote feed ${feedId}`, {
      cleanedUpMids,
    });

    this.updateRedux();
  };

  addStreamTrack = (sourceHandle: string, track: MediaStreamTrack, mid: SubscriberMid) => {
    const feedId = this.feedIdByMid[mid];

    logger
      .remote({ tier: 1 })
      .info(`Received remote track from Janus, mid=${mid}, feedId=${feedId}`);

    if (feedId === undefined) {
      // ignore bs track;
      return;
    }

    const streamMedia = this.media.subscriberStream(feedId, mid);

    if (!streamMedia) {
      return;
    }

    // covering a case when mid was freed and someone else was put on it, we need to remove stale record from collection;
    const oldMedia = Object.entries(this.media.streams).find(
      ([mediaFeedId, media]) =>
        mediaFeedId !== feedId &&
        Object.values(media.streams).find((stream) => stream.subscriberMid === mid)
    );

    if (oldMedia) {
      const matchingStream = Object.values(oldMedia[1].streams).find(
        (stream) => stream.subscriberMid === mid
      );

      if (matchingStream) {
        const message = `${matchingStream?.kind} mid ${mid} was reassigned from feed=${oldMedia[0]} to feed=${feedId}, removing old entry`;
        logger.remote({ capture: 'streaming' }).warn(message);

        if (matchingStream.kind === 'audio') {
          // Mid now serves a different feed; a stale budget would exhaust it instantly.
          this.clearAudioRecoveryState(mid, oldMedia[0]);
          delete this.sinkIdAbortedByMid[mid];
        }

        this.media.deleteStream(oldMedia[0], matchingStream.publisherMid);
      }
    }

    let trackToRemove: MediaStreamTrack | null = null;
    let replacedDeadTrack = false;

    if (streamMedia.hasTrack) {
      const existingTrack = streamMedia.getTrack();
      const existingAlive = existingTrack?.readyState === 'live';

      if (RTCClient.receiveMode === 'videoroom') {
        const isSameTrack = streamMedia.originalTrackId === track.id;

        if (isSameTrack && existingAlive) {
          // ICE unmute dedup: janus.js calls onremotetrack again when tracks unmute
          // after ICE negotiation. We already have the same live track, so we ignore.
          logger
            .remote({ system: true, capture: 'streaming' })
            .debug(
              '[STREAM-DEBUG] Duplicate onremotetrack ignored — same track, both live (ICE unmute dedup)',
              {
                feedId,
                mid,
                trackId: track.id,
                subscriptionState: streamMedia.subscriptionState,
              }
            );
          return;
        }

        // Different track or existing track ended — allow replacement
        logger
          .remote({ capture: 'streaming' })
          .info(
            `[STREAM-DEBUG] Replacing track: isSameTrack=${isSameTrack}, existingAlive=${existingAlive}`,
            {
              feedId,
              mid,
              originalTrackId: streamMedia.originalTrackId,
              existingTrackId: existingTrack?.id,
              existingReadyState: existingTrack?.readyState,
              newTrackId: track.id,
              newTrackReadyState: track.readyState,
              subscriptionState: streamMedia.subscriptionState,
            }
          );
      }

      logger
        .remote({ capture: 'streaming' })
        .info(
          `Replacing controlled track with mid: ${mid}, in feed ${feedId}, with a new instance`,
          {
            track,
            feedId,
            mid,
          }
        );

      trackToRemove = existingTrack ?? null;
      replacedDeadTrack = !!trackToRemove && !existingAlive;
    }

    logger
      .remote({ tier: 1 })
      .info(`Adding track with mid: ${mid}, in feed ${feedId}`, { track, feedId, mid });

    // Add detailed health info when adding audio track
    if (track.kind === 'audio') {
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug(`[AUDIO-HEALTH] Track being added`, {
          mid,
          feedId,
          trackId: track.id,
          trackKind: track.kind,
          trackReadyState: track.readyState,
          trackEnabled: track.enabled,
          trackMuted: track.muted,
          trackLabel: track.label,
          replacingTrack: !!trackToRemove,
          replacedTrackId: trackToRemove?.id,
          existingAudioElement: !!this.audioElements[mid],
          subscriptionState: streamMedia.subscriptionState,
        });
    }

    this.media.setKind(feedId, streamMedia.publisherMid, track.kind as unknown as StreamKind);

    streamMedia.addTrack(track);

    if (trackToRemove) {
      streamMedia.removeTrack(trackToRemove);
    }

    streamMedia.setReceivingState(ReceivingFeedStreamSendingState.on);

    streamMedia.setSubscriptionState(StreamSubscriptionState.connected);

    this.updateRedux();

    // For audio tracks, log immediate post-addition state
    if (track.kind === 'audio') {
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug(`[AUDIO-HEALTH] Track added successfully`, {
          mid,
          feedId,
          trackId: track.id,
          streamHasTrack: streamMedia.hasTrack,
          streamTrackId: streamMedia.getTrack()?.id,
          mediaStreamActive: streamMedia.mediaStream?.active,
          mediaStreamTrackCount: streamMedia.mediaStream?.getTracks().length,
          audioElementExists: !!this.audioElements[mid],
        });
    }

    // Break-before-make: the old PC's close ended the clone the element was playing;
    // the live gate drops the streaming re-flush of the ended original.
    if (track.kind === 'audio' && replacedDeadTrack && track.readyState === 'live') {
      // eslint-disable-next-line no-void
      void this.restartAudioAfterReplace(mid, feedId, track);
    }
  };

  removeStreamTrack = (feedId: FeedId, publisherMid: PublisherMid) => {
    const stream = this.media.stream(feedId, publisherMid);
    const mid = stream.subscriberMid;

    if (!(feedId && mid)) {
      return;
    }

    const message = `Removing track with mid: ${mid}, in feed ${feedId}`;
    Sentry.addBreadcrumb({
      category: 'streaming',
      message,
      data: { feedId, mid },
    });

    logger.remote({ tier: 1 }).info(`Removing track with mid: ${mid}, in feed ${feedId}`);

    // Log detailed state for audio track removal
    if (stream?.kind === 'audio') {
      const track = stream.getTrack();
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug(`[AUDIO-HEALTH] Track being removed`, {
          mid,
          feedId,
          trackId: track?.id,
          trackReadyState: track?.readyState,
          trackEnabled: track?.enabled,
          trackMuted: track?.muted,
          hasAudioElement: !!this.audioElements[mid],
          subscriptionState: stream.subscriptionState,
        });
    }

    const feed = this.receiver?.plugin;

    if (!feed) {
      return;
    }

    this.receiver?.unsubscribe(feedId, mid);

    const tracks = stream?.mediaStream.getTracks();

    // Cleanup controlled track copies. Janus will handle original tracks
    tracks?.forEach((track) => {
      track.stop();
    });

    this.removeOriginalTrack(feed, stream?.originalTrackId);

    this.media.deleteStream(feedId, publisherMid);

    this.updateRedux();
    if (stream.kind === 'audio') {
      this.unloadAudio(mid);
      // Element is gone for good, not a recovery reset — the next attach on this mid
      // must start with a fresh recovery budget (mirrors unsubscribe()).
      this.clearAudioRecoveryState(mid, feedId);
      delete this.sinkIdAbortedByMid[mid];
    } else {
      clearTimeout(this.videoHealthTimers[mid]);
      delete this.videoHealthTimers[mid];
      delete this.videoElements[mid];
    }
  };

  // Diagnostic method to dump complete media state
  logMediaStreamsDiagnostics = (
    reason: string,
    filterKind?: 'audio' | 'video',
    changedStream?: { feedId: string; mid: string }
  ) => {
    // Get local feed IDs to filter them out from diagnostics
    // Local feeds are in MediaList for technical reasons but are never subscribed
    const localFeedIds = new Set<string>();
    if (RTCClient.publishingFeed.handle?.feedId) {
      localFeedIds.add(RTCClient.publishingFeed.handle.feedId);
    }
    if (RTCClient.screensharingFeed.handle?.feedId) {
      localFeedIds.add(RTCClient.screensharingFeed.handle.feedId);
    }

    logMediaStreamsDiagnostics(
      this.media,
      this.audioElements,
      this.videoElements,
      reason,
      localFeedIds,
      filterKind,
      changedStream
    );
  };

  cleanupFeed = (requestingInstanceId?: string) => {
    // Prevent cleanup from affecting wrong instance during room transitions
    if (requestingInstanceId && requestingInstanceId !== this.instanceId) {
      return;
    }

    this.media.forEachFeed((feed) => {
      feed.forEachStream((stream) => {
        if (stream.subscriberMid && this.audioElements[stream.subscriberMid]) {
          this.unloadAudio(stream.subscriberMid);
        }

        const tracks = stream.mediaStream.getTracks();

        if (tracks.length) {
          tracks.forEach((t) => {
            t.stop();
          });
        }
      });
    });

    this.media = new MediaList();

    this.feedIdByMid = {};

    Object.values(this.audioMonitors).forEach((m) => m.dispose());
    this.audioMonitors = {};
    this.audioRecoveryAttempts = {};
    this.sinkIdAbortedByMid = {};
    this.audioStuckReported = {};
    Object.values(this.videoHealthTimers).forEach(clearTimeout);
    this.videoHealthTimers = {};
    this.videoElements = {};

    this.e2eeQueue = {};

    this.disposed = true;
    this.cleanupGeneration += 1;
    this.blockedAudioElements.clear();
    this.blockedVideoElements.clear();

    store.dispatch(remoteFeedDisconnected());

    document.removeEventListener('touchend', this.retryBlockedMedia, true);
    document.removeEventListener('click', this.retryBlockedMedia, true);

    logger.debug('Receiving Feed: cleaned up connection');
  };

  cleanupConnection = (detach = false) => {
    logger.debug('Receiving Feed: cleaning up connection');

    const feed = this.receiver?.plugin;

    if (feed && RTCClient.isMediaServerError) {
      this.cleanupFeed();
      return;
    }

    if (!feed) {
      return;
    }

    if (feed.connectionState === VideoroomConnectionState.disconnecting) {
      return;
    }

    feed.setConnectionState(VideoroomConnectionState.disconnecting);
    if (detach) {
      feed.janusPlugin.detach();
    } else {
      feed.janusPlugin.hangup();
    }
  };

  cleanupSubscription = async (sourceId: FeedId) => {
    this.receiver?.unsubscribe(sourceId);
  };

  resetUserStreams = async (userId: UserId) => {
    const feedIdByUserId = invert(RTCClient.userIdByFeedId);
    const screensharingFeedIdByUserId = invert(RTCClient.userIdByScreensharingFeedId);

    const feedId = feedIdByUserId[userId];
    const screensharingFeedId = screensharingFeedIdByUserId[userId];

    if (feedId) {
      this.media.deleteFeed(feedId);
      logger.remote().log(`Removing stale media for feed=${feedId}, userId=${userId}`);
    }

    if (screensharingFeedId) {
      this.media.deleteFeed(screensharingFeedId);
      logger
        .remote()
        .log(
          `Removing stale media for screensharing feed=${screensharingFeedId}, userId=${userId}`
        );
    }
  };

  updateRedux = () => {
    const state = this.uiState;
    store.dispatch(remoteFeedUpdated(state));
  };

  getBaseSimulcastConfig = (newUsers: number) => {
    const deviceType = selectDeviceType(store.getState());
    const tileWidth = selectTileWidth(store.getState());

    if (deviceType === 'mobile' || tileWidth <= 290) {
      return [0, 2];
    }

    if (newUsers <= 4) {
      return [2, 2];
    }

    return [0, 1];
  };

  getSpeakerSimulcastConfig = () => {
    const deviceType = selectDeviceType(store.getState());
    const tileWidth = selectTileWidth(store.getState());

    if (tileWidth <= 290) {
      return [
        [0, 2],
        [0, 2],
        [0, 2],
      ];
    }

    if (deviceType === 'mobile') {
      return [
        [1, 2],
        [0, 2],
        [0, 2],
      ];
    }

    return [
      [2, 2],
      [1, 2],
      [1, 1],
    ];
  };

  private sendSimulcastUpdate = async (configuredStreams: ConfigureSimulcastTemplate) => {
    const janusPlugin = this.receiver?.plugin?.janusPlugin;

    if (!janusPlugin) {
      return;
    }

    try {
      await sendMessage(janusPlugin, updateSimulcastTemplate(configuredStreams));
    } catch (error) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .warn('Simulcast update failed, likely mid-reconnect', { error: String(error) });
    }
  };

  updateSimulcastConfig = async () => {
    const state = store.getState();

    const speakers = selectHistoricalSpeakers(state);
    const streams = selectOrderedStreams(state);
    const maximizedStream = selectMaximizedStream(state);

    const feedIdByUserId = invert(RTCClient.userIdByFeedId);
    const feedsWithVideo = this.media.feedsWithVideo.length;

    const defaultConfig = this.getBaseSimulcastConfig(feedsWithVideo);
    const speakerConfig = this.getSpeakerSimulcastConfig();

    const specialConfig: Record<UserId, number[]> = {};

    if (feedsWithVideo > 4) {
      speakers.slice(0, 2).forEach((speaker, idx) => {
        specialConfig[speaker.userId] = speakerConfig[idx];
      });
    }

    if (maximizedStream) {
      specialConfig[maximizedStream.userId] = [2, 2];
    }

    const configuredStreams: ConfigureSimulcastTemplate = [];

    for (const stream of streams) {
      const feedId = feedIdByUserId[stream.userId];
      const streamDetails = this.media.getFeedStreams(feedId);

      streamDetails.forEachStream((source) => {
        if (source.subscribed && source.subscriberMid) {
          const [substream, temporal] = specialConfig[stream.userId]
            ? specialConfig[stream.userId]
            : defaultConfig;

          if (source.simulcast.temporal !== temporal || source.simulcast.substream !== substream) {
            source.setSimulcastValues(substream, temporal);
            configuredStreams.push({ mid: source.subscriberMid, substream, temporal });
          }
        }
      });
    }

    if (configuredStreams.length) {
      await this.sendSimulcastUpdate(configuredStreams);

      this.updateRedux();
    }
  };

  getBaseScreenshareSimulcastConfig = () => {
    const state = store.getState();

    const screenshares = selectScreenshareStreams(state);
    const showAllScreens = selectShowAllScreens(state);

    if (!showAllScreens || screenshares.length < 6) {
      return [1, 2];
    }

    return [0, 2];
  };

  updateScreenshareSimulcastConfig = async () => {
    const state = store.getState();

    const showAllScreens = selectShowAllScreens(state);
    const screenshares = selectScreenshareStreams(state);

    const [substream, temporal] = this.getBaseScreenshareSimulcastConfig();

    const configuredStreams: ConfigureSimulcastTemplate = [];

    if (showAllScreens) {
      for (const screenshare of screenshares) {
        if (screenshare.feedId) {
          const streamDetails = this.media.getFeedStreams(screenshare.feedId);
          streamDetails.forEachStream((source) => {
            if (source.subscribed && source.subscriberMid) {
              if (
                source.simulcast.temporal !== temporal ||
                source.simulcast.substream !== substream
              ) {
                source.setSimulcastValues(substream, temporal);
                configuredStreams.push({ mid: source.subscriberMid, substream, temporal });
              }
            }
          });
        }
      }
    } else {
      const selectedScreenshare = selectSelectedScreenshare(state);

      if (selectedScreenshare) {
        const streamDetails = this.media.getFeedStreams(selectedScreenshare.feedId);

        streamDetails.forEachStream((source) => {
          if (source.subscribed && source.subscriberMid) {
            if (
              source.simulcast.temporal !== temporal ||
              source.simulcast.substream !== substream
            ) {
              source.setSimulcastValues(substream, temporal);
              configuredStreams.push({ mid: source.subscriberMid, substream, temporal });
            }
          }
        });
      }
    }

    if (configuredStreams.length) {
      await this.sendSimulcastUpdate(configuredStreams);

      this.updateRedux();
    }
  };

  resetSimulcastSettings = () => {
    this.media.forEachFeed((list) => {
      list.forEachStream((stream) => {
        stream.resetSimulcastValues();
      });
    });
  };

  updateSimulcastValue = (mid: SubscriberMid, substream?: number, temporal?: number) => {
    const feedId = this.feedIdByMid[mid];
    const streamMedia = this.media.subscriberStream(feedId, mid);

    try {
      streamMedia?.setSimulcastValues(substream, temporal);
    } catch (error) {
      const message = `Couldn't update simulcast in ${feedId} for mid ${mid}, error:`;
      Sentry.captureMessage(message, 'warning');
      logger.remote({ tier: 1 }).error(message, error);
    }

    this.updateRedux();
  };

  subscribeToE2eeUser = async (userId: UserId) => {
    const pendingSubscription = this.e2eeQueue[userId];

    if (pendingSubscription) {
      logger
        .remote({ tier: 1 })
        .debug(`Releasing pending E2EE subscription for the userId=${userId}`);

      // The request now awaits the round trip, so a rejection would otherwise
      // leave the entry behind for the next release to send a second time.
      delete this.e2eeQueue[userId];

      await this.requestSubscription(
        pendingSubscription.connectionHandle,
        pendingSubscription.streams
      );
    }
  };

  removeE2eePendingSubscription = ({ userId, mid, feedId }: E2EEQueueUpdateOptions) => {
    const pendingSubscription = this.e2eeQueue[userId];
    if (pendingSubscription) {
      logger
        .remote({ tier: 1 })
        .debug(
          `Removing pending E2EE subscription for userId=${userId} with feedId=${feedId} on mid=${mid}`
        );

      pendingSubscription.streams = pendingSubscription.streams.filter(
        (stream) => !(stream.feed === feedId && stream.mid === mid)
      );
    }
  };

  private setAudioElementOutput = async (
    element: AudioElement,
    audioOutputId: string | undefined
  ) => {
    if (!element) {
      const errorMessage = 'ReceivingFeed: setAudioElementOutput called with null element';
      logger.remote({ system: true, capture: 'streaming' }).warn(errorMessage, {
        audioOutputId,
      });

      Sentry.captureMessage(errorMessage, {
        level: 'warning',
        tags: { component: 'ReceivingFeed', operation: 'setAudioElementOutput' },
        extra: { audioOutputId },
      });

      return;
    }

    if (!element.setSinkId) {
      return;
    }

    const currentSinkId = element.sinkId || '';
    const targetSinkId = audioOutputId || '';

    // We don't need to set the sink id if it's already the same as the target sink id
    if (currentSinkId === targetSinkId) {
      return;
    }

    if (!audioOutputId) {
      logger.debug('ReceivingFeed: no audio output device specified, using default', {
        elementId: element.id,
        currentSinkId,
      });

      return;
    }

    try {
      await element.setSinkId(audioOutputId);

      // Verify the sink was set correctly
      if (element.sinkId !== audioOutputId) {
        logger
          .remote({ system: true, capture: 'streaming' })
          .warn('[AUDIO-PLAYBACK] audio output device mismatch after setSinkId', {
            elementId: element.id,
            requested: audioOutputId,
            actual: element.sinkId || 'default',
          });
      }
    } catch (error) {
      if (isUserGestureError(error)) {
        const currentDevice = currentSinkId;
        logger
          .remote({ system: true, capture: 'streaming' })
          .debug('[AUDIO-PLAYBACK] setSinkId requires gesture, audio uses default device', {
            elementId: element.id,
            requestedDevice: audioOutputId,
            currentDevice,
            deviceMismatch: currentDevice !== '' && currentDevice !== audioOutputId,
          });
      } else {
        // A failed output-device switch is non-actionable here and doesn't prove lost
        // audio — the playback-health monitor catches inaudible elements. Log a breadcrumb;
        // no Sentry exception (this path was the high-volume MONZA-1F false positive).
        logger
          .remote({ system: true, capture: 'streaming' })
          .warn('[AUDIO-PLAYBACK] setSinkId failed', {
            elementId: element.id,
            requestedDevice: audioOutputId,
            currentSinkId: currentSinkId || 'default',
            ...formatErrorForLogging(error),
          });
      }

      throw error;
    }
  };

  private unloadAudio = (mid: SubscriberMid) => {
    if (this.blockedAudioElements.delete(mid)) {
      this.syncBlockedMediaStateToStore();
    }

    this.audioMonitors[mid]?.dispose();
    delete this.audioMonitors[mid];

    const element = this.audioElements[mid];
    const feedId = this.feedIdByMid[mid];

    if (element) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug('[AUDIO-DEBUG] Started unloading audio element', {
          mid,
          feedId,
          elementId: element.id,
          paused: element.paused,
          ended: element.ended,
          volume: element.volume,
          muted: element.muted,
          sinkId: element.sinkId || 'default',
          srcObjectId: (element.srcObject as MediaStream)?.id,
          srcObjectActive: (element.srcObject as MediaStream)?.active,
        });
    }

    if (element instanceof UnlockedAudio) {
      AudioPool.unloadElement(element as UnlockedAudio);
      delete this.audioElements[mid];

      logger
        .remote({ system: true, capture: 'streaming' })
        .info('[AUDIO-DEBUG] Audio element unloaded successfully', {
          mid,
          feedId,
          elementId: element.id,
        });
    }
  };

  private removeOriginalTrack = (feed: ControlledReceivingHandle, trackId: string) => {
    const { remoteStream } = feed.janusPlugin.webrtcStuff;

    if (remoteStream) {
      const track = remoteStream.getTracks().find((t) => t.id === trackId);
      if (track && this.receiver instanceof VideoroomReceiver) {
        Janus.log('Removing remote track', track);
        remoteStream.removeTrack(track);
      }
    }
  };

  private prepareE2EESubscription = (connectionHandle: string, streams: SubscribeStreamList) => {
    const filteredStreams: SubscribeStreamList = [];

    streams?.forEach((stream) => {
      const { feed } = stream;
      const userId = RTCClient.userIdByFeedId[feed] || RTCClient.userIdByScreensharingFeedId[feed];

      const receiver = E2EEManager.getReceiver(userId, RTCClient.roomId!);
      const keysExchanged = Boolean(receiver?.sharedSecret);
      const sframeReady =
        receiver?.e2eeId != null && SFrameManager.isReceiverReady(receiver.e2eeId);

      if (keysExchanged && sframeReady) {
        filteredStreams.push(stream);
      } else {
        logger
          .remote()
          .debug(
            `E2EE subscription not ready for userId=${userId} of feed=${stream.feed}, ${
              stream.mid && `mid=${stream.mid}`
            }. Enqueue until ${!keysExchanged ? 'keys are exchanged' : 'SFrame receiver is ready'}.`
          );

        this.e2eeQueue[userId] ??= {
          connectionHandle,
          streams: [],
        };
        this.e2eeQueue[userId].streams.push(stream);
      }
    });

    return filteredStreams;
  };
}
